import { isPigmentActionSpec } from "./action.js";
import { isPigmentDataSpec } from "./data.js";
import { isBlockNode, isPigmentJsonValue } from "./model.js";
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isNonEmptyText(value) {
    return typeof value === "string" && value.length > 0 && value === value.trim();
}
function hasOwn(value, key) {
    return Object.prototype.hasOwnProperty.call(value, key);
}
function push(issues, issue) {
    issues.push(issue);
}
function validateSchemaValue(value, schema, path, issues, blockId, property) {
    const invalid = (message) => push(issues, {
        code: "invalid_prop", severity: "error", path, message, blockId, property,
    });
    const primitiveKind = schema.kind;
    if (primitiveKind === "json") {
        // JSON portability has already been checked by the document-shape pass.
    }
    else if (primitiveKind === "array") {
        if (!Array.isArray(value))
            invalid("Expected an array value.");
        else if (schema.items)
            value.forEach((item, index) => validateSchemaValue(item, schema.items, `${path}[${index}]`, issues, blockId, property));
    }
    else if (primitiveKind === "object") {
        if (!isRecord(value))
            invalid("Expected an object value.");
        else if (schema.properties) {
            for (const [key, propertySchema] of Object.entries(schema.properties)) {
                if (propertySchema.required && !hasOwn(value, key)) {
                    push(issues, {
                        code: "required_prop_missing", severity: "error", path: `${path}.${key}`,
                        message: "Required object property is missing.", blockId, property,
                    });
                }
            }
            for (const [key, propertyValue] of Object.entries(value)) {
                const propertySchema = schema.properties[key];
                if (!propertySchema) {
                    push(issues, {
                        code: "unknown_prop", severity: "error", path: `${path}.${key}`,
                        message: "Property is not declared by this object schema.", blockId, property,
                    });
                }
                else
                    validateSchemaValue(propertyValue, propertySchema, `${path}.${key}`, issues, blockId, property);
            }
        }
    }
    else if (primitiveKind === "number") {
        if (typeof value !== "number" || !Number.isFinite(value))
            invalid("Expected a finite number value.");
    }
    else if (primitiveKind === "boolean") {
        if (typeof value !== "boolean")
            invalid("Expected a boolean value.");
    }
    else if (typeof value !== "string" || (primitiveKind === "id" && !isNonEmptyText(value))) {
        invalid(primitiveKind === "id" ? "Expected a non-empty trimmed id." : "Expected a string value.");
    }
    else if (primitiveKind === "url" && (value !== value.trim() || (schema.required && value.length === 0))) {
        invalid(schema.required ? "Expected a non-empty trimmed URL value." : "Expected an empty or trimmed URL value.");
    }
    else if (schema.format === "date" && !isRfc3339FullDate(value)) {
        invalid("Expected an RFC 3339 full-date value (YYYY-MM-DD).");
    }
    if (schema.enum && !schema.enum.some((member) => member === value))
        invalid("Value is not one of the declared enum members.");
}
function isRfc3339FullDate(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);
    if (!match)
        return false;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (month < 1 || month > 12 || day < 1)
        return false;
    const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    const daysInMonth = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return day <= daysInMonth[month - 1];
}
function manifestBlockMap(manifest) {
    return new Map(manifest.blocks.map((block) => [block.type, block]));
}
function createAuthoredAssetValidationState(manifest) {
    return {
        metadataByReference: new Map((manifest.assets ?? []).map((asset) => [asset.reference, asset])),
        seenReferences: new Set(),
        uniqueTransferBytes: 0,
    };
}
function validateAuthoredAsset(reference, property, diagnosticPath, blockId, manifest, state, issues) {
    if (state.seenReferences.has(reference))
        return;
    const priorReferenceCount = state.seenReferences.size;
    const priorTransferBytes = state.uniqueTransferBytes;
    state.seenReferences.add(reference);
    const metadata = state.metadataByReference.get(reference);
    if (!metadata && manifest.validation?.assets?.requireMetadata) {
        push(issues, {
            code: "asset_metadata_missing", severity: "error", path: diagnosticPath, blockId, property,
            message: `Authored asset "${reference}" has no matching manifest metadata.`,
        });
    }
    const budget = manifest.validation?.authoredAssetBudget;
    if (!budget)
        return;
    if (metadata) {
        if (budget.maxSingleAssetBytes !== undefined && metadata.byteSize > budget.maxSingleAssetBytes) {
            push(issues, {
                code: "asset_single_byte_budget_exceeded", severity: "error", path: diagnosticPath, blockId, property,
                message: `Authored asset is ${metadata.byteSize} bytes; the per-asset budget is ${budget.maxSingleAssetBytes} bytes.`,
            });
        }
        state.uniqueTransferBytes += metadata.byteSize;
    }
    const referenceCount = state.seenReferences.size;
    if (budget.maxUniqueReferences !== undefined
        && priorReferenceCount <= budget.maxUniqueReferences
        && referenceCount > budget.maxUniqueReferences) {
        push(issues, {
            code: "asset_reference_budget_exceeded", severity: "error", path: diagnosticPath, blockId, property,
            message: `Authored asset reference count is ${referenceCount}; the budget is ${budget.maxUniqueReferences}.`,
        });
    }
    if (metadata
        && budget.maxUniqueTransferBytes !== undefined
        && priorTransferBytes <= budget.maxUniqueTransferBytes
        && state.uniqueTransferBytes > budget.maxUniqueTransferBytes) {
        push(issues, {
            code: "asset_transfer_budget_exceeded", severity: "error", path: diagnosticPath, blockId, property,
            message: `Unique authored-asset transfer is ${state.uniqueTransferBytes} bytes; the budget is ${budget.maxUniqueTransferBytes} bytes.`,
        });
    }
}
function validateResourceReference(rawReference, kind, diagnosticPath, blockId, property, manifest, issues) {
    const policy = kind === "link" ? manifest.validation?.links : manifest.validation?.assets;
    if (!policy)
        return; // Manifest validation rejects this before conformance.
    const reference = rawReference.trim();
    // A network-path reference is identified by syntax, not by the origin it
    // happens to resolve to. Comparing against a sentinel origin fails when the
    // authored host is the sentinel itself, and WHATWG treats slash/backslash
    // mixtures as network-path separators for special schemes.
    const protocolRelative = /^[\\/]{2}/u.test(reference);
    let parsed;
    let absolute = true;
    try {
        // The WHATWG parser, unlike a scheme regex, correctly treats embedded
        // ASCII tab/CR/LF as ignorable inside a scheme. That keeps strings such as
        // `java\nscript:` from masquerading as relative references.
        parsed = new URL(reference);
    }
    catch {
        absolute = false;
        try {
            parsed = new URL(reference, "https://pigment-relative.invalid/__base__/");
        }
        catch {
            push(issues, {
                code: `invalid_${kind}_reference`,
                severity: "error", path: diagnosticPath, blockId, property,
                message: `Expected a valid ${kind} reference.`,
            });
            return;
        }
    }
    if (!absolute) {
        if (policy.allowRelative && !protocolRelative)
            return;
        push(issues, {
            code: `disallowed_${kind}_${protocolRelative ? "origin" : "scheme"}`,
            severity: "error", path: diagnosticPath, blockId, property,
            message: protocolRelative ? "Protocol-relative resource references are not permitted." : "Relative resource references are not permitted.",
        });
        return;
    }
    const scheme = parsed.protocol.slice(0, -1).toLowerCase();
    if (!policy.allowedSchemes.includes(scheme)) {
        push(issues, {
            code: `disallowed_${kind}_scheme`,
            severity: "error", path: diagnosticPath, blockId, property,
            message: `Scheme "${scheme}:" is not allowed for this ${kind}.`,
        });
        return;
    }
    if ((scheme === "http" || scheme === "https") && policy.allowedOrigins && !policy.allowedOrigins.includes(parsed.origin)) {
        push(issues, {
            code: `disallowed_${kind}_origin`,
            severity: "error", path: diagnosticPath, blockId, property,
            message: `Origin "${parsed.origin}" is not allowed for this ${kind}.`,
        });
    }
}
function invalidActionSpec(issues, path, message, blockId, property) {
    push(issues, { code: "invalid_action_spec", severity: "error", path, message, blockId, property });
}
function validateActionSpec(value, path, definition, manifest, blockId, property, assetState, issues) {
    if (!isPigmentActionSpec(value)) {
        if (!isRecord(value)) {
            invalidActionSpec(issues, path, "Expected a portable action object.", blockId, property);
            return;
        }
        for (const key of Object.keys(value)) {
            if (key !== "do" && key !== "args")
                invalidActionSpec(issues, `${path}.${key}`, "Unknown action key.", blockId, property);
        }
        if (!isNonEmptyText(value.do)) {
            invalidActionSpec(issues, `${path}.do`, "Action capability names must be non-empty trimmed text.", blockId, property);
        }
        if (value.args !== undefined && (!isRecord(value.args) || !isPigmentActionSpec({ do: "probe", args: value.args }))) {
            invalidActionSpec(issues, `${path}.args`, "Action arguments must be a portable JSON object.", blockId, property);
        }
        return;
    }
    const capability = manifest.actionCapabilities?.find((candidate) => candidate.name === value.do);
    if (!capability) {
        push(issues, {
            code: "unknown_action_capability", severity: "error", path: `${path}.do`, blockId, property,
            message: `Action capability "${value.do}" is not declared by this manifest.`,
        });
        return;
    }
    if (!definition.actionCapabilities?.includes(value.do)) {
        push(issues, {
            code: "disallowed_action_capability", severity: "error", path: `${path}.do`, blockId, property,
            message: `Block type "${definition.type}" is not granted action capability "${value.do}".`,
        });
    }
    if (capability.deprecated) {
        push(issues, {
            code: "deprecated_action_capability", severity: "warning", path: `${path}.do`, blockId, property,
            message: `Action capability "${value.do}" is deprecated: ${capability.deprecated.message}`,
            replacement: capability.deprecated.replacement,
        });
    }
    const args = value.args ?? {};
    const input = capability.input ?? {};
    for (const [key, schema] of Object.entries(input)) {
        if (schema.required && !hasOwn(args, key)) {
            push(issues, {
                code: "invalid_action_input", severity: "error", path: `${path}.args.${key}`, blockId, property,
                message: `Required argument "${key}" is missing for action capability "${value.do}".`,
            });
        }
    }
    for (const [key, argument] of Object.entries(args)) {
        const schema = input[key];
        const argumentPath = `${path}.args.${key}`;
        if (!schema) {
            push(issues, {
                code: "invalid_action_input", severity: "error", path: argumentPath, blockId, property,
                message: `Argument "${key}" is not declared by action capability "${value.do}".`,
            });
            continue;
        }
        const typeIssues = [];
        validateSchemaValue(argument, schema, argumentPath, typeIssues, blockId, property);
        for (const finding of typeIssues)
            push(issues, { ...finding, code: "invalid_action_input" });
        validateSchemaSemantics(argument, schema, argumentPath, definition, manifest, blockId, property, assetState, issues);
    }
}
function invalidDataSpec(issues, path, message, blockId, property) {
    push(issues, { code: "invalid_data_spec", severity: "error", path, message, blockId, property });
}
function validateDataSpec(value, path, definition, manifest, blockId, property, assetState, issues) {
    if (!isPigmentDataSpec(value)) {
        if (!isRecord(value)) {
            invalidDataSpec(issues, path, "Expected a portable data-call object.", blockId, property);
            return;
        }
        for (const key of Object.keys(value)) {
            if (key !== "get" && key !== "args")
                invalidDataSpec(issues, `${path}.${key}`, "Unknown data-call key.", blockId, property);
        }
        if (!isNonEmptyText(value.get)) {
            invalidDataSpec(issues, `${path}.get`, "Data capability names must be non-empty trimmed text.", blockId, property);
        }
        if (value.args !== undefined && (!isRecord(value.args) || !isPigmentDataSpec({ get: "probe", args: value.args }))) {
            invalidDataSpec(issues, `${path}.args`, "Data arguments must be a portable JSON object.", blockId, property);
        }
        return;
    }
    const capability = manifest.dataCapabilities?.find((candidate) => candidate.name === value.get);
    if (!capability) {
        push(issues, {
            code: "unknown_data_capability", severity: "error", path: `${path}.get`, blockId, property,
            message: `Data capability "${value.get}" is not declared by this manifest.`,
        });
        return;
    }
    if (!definition.dataCapabilities?.includes(value.get)) {
        push(issues, {
            code: "disallowed_data_capability", severity: "error", path: `${path}.get`, blockId, property,
            message: `Block type "${definition.type}" is not granted data capability "${value.get}".`,
        });
    }
    if (!capability.output) {
        push(issues, {
            code: "missing_data_output_schema", severity: "error", path: `${path}.get`, blockId, property,
            message: `Data capability "${value.get}" has no declared output schema.`,
        });
    }
    if (capability.deprecated) {
        push(issues, {
            code: "deprecated_data_capability", severity: "warning", path: `${path}.get`, blockId, property,
            message: `Data capability "${value.get}" is deprecated: ${capability.deprecated.message}`,
            replacement: capability.deprecated.replacement,
        });
    }
    const args = value.args ?? {};
    const input = capability.input ?? {};
    for (const [key, schema] of Object.entries(input)) {
        if (schema.required && !hasOwn(args, key)) {
            push(issues, {
                code: "invalid_data_input", severity: "error", path: `${path}.args.${key}`, blockId, property,
                message: `Required argument "${key}" is missing for data capability "${value.get}".`,
            });
        }
    }
    for (const [key, argument] of Object.entries(args)) {
        const schema = input[key];
        const argumentPath = `${path}.args.${key}`;
        if (!schema) {
            push(issues, {
                code: "invalid_data_input", severity: "error", path: argumentPath, blockId, property,
                message: `Argument "${key}" is not declared by data capability "${value.get}".`,
            });
            continue;
        }
        const typeIssues = [];
        validateSchemaValue(argument, schema, argumentPath, typeIssues, blockId, property);
        for (const finding of typeIssues)
            push(issues, { ...finding, code: "invalid_data_input" });
        validateSchemaSemantics(argument, schema, argumentPath, definition, manifest, blockId, property, assetState, issues);
    }
}
function validateSchemaSemantics(value, schema, path, definition, manifest, blockId, property, assetState, issues) {
    if (schema.capabilityBinding === "action") {
        validateActionSpec(value, path, definition, manifest, blockId, property, assetState, issues);
        return;
    }
    if (schema.capabilityBinding === "data") {
        validateDataSpec(value, path, definition, manifest, blockId, property, assetState, issues);
        return;
    }
    if (schema.resourceKind && typeof value === "string" && value.trim() !== "") {
        const reference = value.trim();
        if (schema.resourceKind === "asset") {
            validateAuthoredAsset(reference, property, path, blockId, manifest, assetState, issues);
        }
        validateResourceReference(reference, schema.resourceKind, path, blockId, property, manifest, issues);
    }
    if (schema.kind === "array" && Array.isArray(value) && schema.items) {
        value.forEach((item, index) => validateSchemaSemantics(item, schema.items, `${path}[${index}]`, definition, manifest, blockId, property, assetState, issues));
    }
    else if (schema.kind === "object" && isRecord(value) && schema.properties) {
        for (const [key, childSchema] of Object.entries(schema.properties)) {
            if (hasOwn(value, key))
                validateSchemaSemantics(value[key], childSchema, `${path}.${key}`, definition, manifest, blockId, property, assetState, issues);
        }
    }
}
function validateBlockPolicies(props, definition, manifest, path, issues, blockId, assetState) {
    for (const binding of definition.dataBindings ?? []) {
        if (!hasOwn(props, binding.property))
            continue;
        for (const conflictingProperty of binding.mutuallyExclusiveWith ?? []) {
            if (!hasOwn(props, conflictingProperty))
                continue;
            push(issues, {
                code: "conflicting_data_source", severity: "error",
                path: `${path}.props.${conflictingProperty}`, blockId, property: conflictingProperty,
                message: `Prop "${conflictingProperty}" cannot be authored with data binding "${binding.property}".`,
            });
        }
    }
    // Asset governance is a separate authored-content contract from URL policy.
    // Running it independently keeps missing metadata and byte budgets visible
    // even when the same reference also violates a scheme or origin policy.
    for (const binding of definition.resourceBindings ?? []) {
        if (binding.kind !== "asset")
            continue;
        const value = props[binding.property];
        if (typeof value !== "string" || value.trim() === "")
            continue;
        validateAuthoredAsset(value.trim(), binding.property, `${path}.props.${binding.property}`, blockId, manifest, assetState, issues);
    }
    for (const binding of definition.resourceBindings ?? []) {
        const value = props[binding.property];
        if (typeof value !== "string" || value.trim() === "")
            continue;
        validateResourceReference(value, binding.kind, `${path}.props.${binding.property}`, blockId, binding.property, manifest, issues);
    }
    const alternative = definition.accessibility?.imageAlternative;
    if (alternative) {
        const source = props[alternative.sourceProperty];
        if (typeof source === "string" && source.trim() !== "") {
            const hasAlternative = hasOwn(props, alternative.alternativeProperty);
            const alternativeValue = props[alternative.alternativeProperty];
            if (!hasAlternative || typeof alternativeValue !== "string" || (!alternative.allowEmptyAlternative && alternativeValue.trim() === "")) {
                push(issues, {
                    code: "missing_image_alternative", severity: "error",
                    path: `${path}.props.${alternative.alternativeProperty}`, blockId, property: alternative.alternativeProperty,
                    message: alternative.allowEmptyAlternative
                        ? "An authored image source requires explicit alternative text; use an empty string only for a decorative image."
                        : "An authored image source requires non-empty alternative text.",
                });
            }
        }
    }
    const responsive = definition.responsive;
    if (responsive) {
        const columns = props[responsive.columnsProperty];
        if (typeof columns === "string" && columns.trim() !== "" && !responsive.allowedExplicitTemplates?.includes(columns.trim())) {
            push(issues, {
                code: "unsafe_responsive_layout", severity: "error",
                path: `${path}.props.${responsive.columnsProperty}`, blockId, property: responsive.columnsProperty,
                message: "Explicit grid template is not declared narrow-viewport safe; omit it to use intrinsic layout.",
            });
        }
        const measure = props[responsive.minColumnWidthProperty];
        if (typeof measure === "string" && measure.trim() !== "") {
            const match = /^(\d+(?:\.\d+)?)px$/u.exec(measure.trim());
            const pixels = match ? Number(match[1]) : Number.NaN;
            if (!Number.isFinite(pixels) || pixels < responsive.minColumnWidthPx || pixels > responsive.maxColumnWidthPx) {
                push(issues, {
                    code: "invalid_responsive_measure", severity: "error",
                    path: `${path}.props.${responsive.minColumnWidthProperty}`, blockId, property: responsive.minColumnWidthProperty,
                    message: `Intrinsic grid width must be a px value between ${responsive.minColumnWidthPx}px and ${responsive.maxColumnWidthPx}px.`,
                });
            }
        }
    }
}
/** Validate a host resolver result before it reaches a block renderer. */
export function validatePigmentDataOutput(value, capability, manifest) {
    if (!capability.output) {
        return { ok: false, issues: [{
                    code: "missing_data_output_schema",
                    path: "$",
                    message: `Data capability "${capability.name}" has no declared output schema.`,
                }] };
    }
    if (!isPigmentJsonValue(value)) {
        return { ok: false, issues: [{
                    code: "invalid_data_output",
                    path: "$",
                    message: `Data capability "${capability.name}" returned a nonportable value.`,
                }] };
    }
    const findings = [];
    const resolverDefinition = {
        type: `data:${capability.name}`,
        dataCapabilities: [capability.name],
        // Data outputs are values, not an ambient authority escalation. Until a
        // data capability explicitly models delegated action grants, nested action
        // calls in resolver output must fail closed.
        actionCapabilities: [],
    };
    validateSchemaValue(value, capability.output, "$", findings, undefined, "$data");
    validateSchemaSemantics(value, capability.output, "$", resolverDefinition, manifest, undefined, "$data", createAuthoredAssetValidationState(manifest), findings);
    if (!findings.length)
        return { ok: true, value, issues: [] };
    return {
        ok: false,
        issues: findings.map((finding) => ({
            code: "invalid_data_output",
            path: finding.path,
            message: finding.message,
        })),
    };
}
function validateTreeNode(value, path, definitions, manifest, ids, issues, assetState, parent) {
    if (!isBlockNode(value))
        return;
    const blockId = isNonEmptyText(value.id) ? value.id : undefined;
    if (!blockId) {
        push(issues, { code: "invalid_block_id", severity: "error", path: `${path}.id`, message: "Block ids must be non-empty trimmed text." });
    }
    else if (ids.has(blockId)) {
        push(issues, {
            code: "duplicate_block_id", severity: "error", path: `${path}.id`, blockId,
            message: `Duplicate block id "${blockId}"; first declared at ${ids.get(blockId)}.`,
        });
    }
    else
        ids.set(blockId, path);
    const definition = definitions.get(value.type);
    if (!isNonEmptyText(value.type) || !definition) {
        push(issues, {
            code: "unknown_block_type", severity: "error", path: `${path}.type`, blockId,
            message: `Block type "${value.type}" is not declared by this manifest.`,
        });
    }
    if (parent) {
        if (value.slot !== undefined) {
            if (!isNonEmptyText(value.slot)) {
                push(issues, { code: "invalid_slot", severity: "error", path: `${path}.slot`, blockId, message: "Slot names must be non-empty trimmed text." });
            }
            const slot = parent.slots?.find((candidate) => candidate.name === value.slot);
            if (!slot) {
                push(issues, {
                    code: "unknown_slot", severity: "error", path: `${path}.slot`, blockId,
                    message: `Slot "${value.slot}" is not declared by parent block "${parent.type}".`,
                });
            }
            else if (slot.allowedTypes && isNonEmptyText(value.type) && !slot.allowedTypes.includes(value.type)) {
                push(issues, {
                    code: "disallowed_child", severity: "error", path: `${path}.type`, blockId,
                    message: `Block type "${value.type}" is not allowed in slot "${slot.name}".`,
                });
            }
        }
        else if (parent.allowedChildren) {
            if (isNonEmptyText(value.type) && !parent.allowedChildren.includes(value.type))
                push(issues, {
                    code: "disallowed_child", severity: "error", path: `${path}.type`, blockId,
                    message: `Block type "${value.type}" is not allowed as a child of "${parent.type}".`,
                });
        }
        else if (parent.slots?.length) {
            push(issues, {
                code: "unknown_slot", severity: "error", path: `${path}.slot`, blockId,
                message: `Children of "${parent.type}" must name one of its declared slots.`,
            });
        }
    }
    else if (value.slot !== undefined) {
        push(issues, { code: "root_slot_not_allowed", severity: "error", path: `${path}.slot`, blockId, message: "A root block cannot name a parent slot." });
    }
    if (definition) {
        const schemas = definition.props ?? {};
        const props = isRecord(value.props) ? value.props : {};
        for (const [key, schema] of Object.entries(schemas)) {
            if (schema.required && !hasOwn(props, key)) {
                push(issues, {
                    code: "required_prop_missing", severity: "error", path: `${path}.props.${key}`, blockId, property: key,
                    message: "Required prop is missing.",
                });
            }
        }
        for (const [key, propValue] of Object.entries(props)) {
            const schema = schemas[key];
            if (!schema) {
                push(issues, {
                    code: "unknown_prop", severity: "error", path: `${path}.props.${key}`, blockId, property: key,
                    message: "Prop is not declared by this block type.",
                });
            }
            else {
                validateSchemaValue(propValue, schema, `${path}.props.${key}`, issues, blockId, key);
                validateSchemaSemantics(propValue, schema, `${path}.props.${key}`, definition, manifest, blockId, key, assetState, issues);
                if (schema.deprecated) {
                    push(issues, {
                        code: "deprecated_prop", severity: "warning", path: `${path}.props.${key}`, blockId, property: key,
                        message: `Prop "${key}" is deprecated: ${schema.deprecated.message}`,
                        replacement: schema.deprecated.replacement,
                    });
                }
            }
        }
        if (definition.deprecated) {
            push(issues, {
                code: "deprecated_block", severity: "warning", path: `${path}.type`, blockId,
                message: `Block type "${definition.type}" is deprecated: ${definition.deprecated.message}`,
                replacement: definition.deprecated.replacement,
            });
        }
        validateBlockPolicies(props, definition, manifest, path, issues, blockId, assetState);
    }
    if (!Array.isArray(value.children))
        return;
    const slotCounts = new Map();
    for (const [index, child] of value.children.entries()) {
        if (isRecord(child) && isNonEmptyText(child.slot))
            slotCounts.set(child.slot, (slotCounts.get(child.slot) ?? 0) + 1);
        validateTreeNode(child, `${path}.children[${index}]`, definitions, manifest, ids, issues, assetState, definition);
    }
    if (definition?.slots) {
        for (const slot of definition.slots) {
            const count = slotCounts.get(slot.name) ?? 0;
            if (slot.minChildren !== undefined && count < slot.minChildren) {
                push(issues, {
                    code: "slot_min_children", severity: "error", path: `${path}.children`, blockId,
                    message: `Slot "${slot.name}" requires at least ${slot.minChildren} child block(s).`,
                });
            }
            if (slot.maxChildren !== undefined && count > slot.maxChildren) {
                push(issues, {
                    code: "slot_max_children", severity: "error", path: `${path}.children`, blockId,
                    message: `Slot "${slot.name}" allows at most ${slot.maxChildren} child block(s).`,
                });
            }
        }
    }
}
function validateTreeRoots(value, path, definitions, manifest, ids, issues, assetState) {
    if (!Array.isArray(value))
        return;
    value.forEach((node, index) => validateTreeNode(node, `${path}[${index}]`, definitions, manifest, ids, issues, assetState));
}
/** Validate semantic block/slot/prop conformance after document shape and manifest validity are established. */
export function validatePigmentDocumentConformance(document, manifest) {
    const issues = [];
    const definitions = manifestBlockMap(manifest);
    const ids = new Map();
    const assetState = createAuthoredAssetValidationState(manifest);
    validateTreeRoots(document.blocks, "$.blocks", definitions, manifest, ids, issues, assetState);
    if (document.chrome && typeof document.chrome === "object") {
        for (const slot of ["announcement", "header", "footer"]) {
            const override = document.chrome[slot];
            if (override)
                validateTreeRoots(override.blocks, `$.chrome.${slot}.blocks`, definitions, manifest, ids, issues, assetState);
        }
    }
    return issues;
}
/** The same canonical walk, rooted at a manifest's example paths. */
export function validatePigmentManifestExampleConformance(manifest) {
    const issues = [];
    const definitions = manifestBlockMap(manifest);
    for (const [blockIndex, block] of manifest.blocks.entries()) {
        if (!block.examples)
            continue;
        for (const [exampleIndex, example] of block.examples.entries()) {
            validateTreeNode(example, `blocks[${blockIndex}].examples[${exampleIndex}]`, definitions, manifest, new Map(), issues, createAuthoredAssetValidationState(manifest));
        }
    }
    if (!manifest.examples)
        return issues;
    for (const [exampleIndex, example] of manifest.examples.entries()) {
        const ids = new Map();
        const assetState = createAuthoredAssetValidationState(manifest);
        validateTreeRoots(example.document.blocks, `examples[${exampleIndex}].document.blocks`, definitions, manifest, ids, issues, assetState);
        if (example.document.chrome) {
            for (const slot of ["announcement", "header", "footer"]) {
                const override = example.document.chrome[slot];
                if (override)
                    validateTreeRoots(override.blocks, `examples[${exampleIndex}].document.chrome.${slot}.blocks`, definitions, manifest, ids, issues, assetState);
            }
        }
    }
    return issues;
}
